/**
 * ResiduePassiveSkillEffect — Poison tree Expel path, Ecdysis branch terminal
 * passive.
 *
 * When Residue is unlocked, Ecdysis leaves a 1-tile radius contact toxin patch
 * (contact_coating zone) at the detonation point for 4 seconds. Each enemy who
 * walks through the patch receives a single poison dose — one application per
 * entity, then the patch ignores them (oneShot). The zone handles
 * reinforcements who arrive after the initial burst clears.
 *
 * The actual zone spawning logic lives in EcdysisSkillEffect.spawnResidue(),
 * which checks resolver.hasEffect('ecdysis_passive', 'residueRadiusTiles')
 * to gate creation. This passive is a data provider: it makes the resolver
 * keys available when the node is unlocked, and exposes the resolved values
 * via getters for external inspection.
 *
 * This is one of two terminal passives required for Synthesis (Apotheosis).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Residue (ecdysis_passive)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ResiduePassiveDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ResiduePassiveConfig {
  /** Fallback radius in tiles if resolver returns 0. */
  readonly defaultRadiusTiles: number;
  /** Fallback duration in ms if resolver returns 0. */
  readonly defaultDurationMs: number;
}

const DEFAULT_CONFIG: ResiduePassiveConfig = {
  defaultRadiusTiles: 1,
  defaultDurationMs: 4000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ResiduePassiveSkillEffect implements SkillEffect {
  readonly skillId = 'ecdysis_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ResiduePassiveDeps;
  private readonly config: ResiduePassiveConfig;

  constructor(deps: ResiduePassiveDeps, config: Partial<ResiduePassiveConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — no per-frame logic
  }

  destroy(): void {
    // Passive — nothing to clean up
  }
}
